import { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Error } from '../../components/error';
import { Statistic } from '../../components/statistic';
import { Habit } from '../../dto/habit';
import { PartnerShipStatus, Partnership } from '../../dto/partnership';
import { useFirebase } from '../../hooks/firebase.hook';
import { useHabit } from '../../hooks/habit.hook';
import { useLayout } from '../../hooks/layout.hook';
import { useNavigation } from '../../hooks/navigation.hook';
import { usePartnership } from '../../hooks/partnership.hook';
import { RecordMap, useRecord } from '../../hooks/record.hook';

export default function PartnerDetailsScreen(): JSX.Element {
  const { navigate } = useNavigation();
  const { t } = useTranslation();
  const location = useLocation();
  const { recordsForPartner } = useRecord();
  const { habitsForPartner } = useHabit();
  const { endPartnership } = usePartnership();
  const { translateError } = useFirebase();

  const [partnerRecords, setPartnerRecords] = useState<RecordMap>();
  const [partnerHabits, setPartnerHabits] = useState<Habit[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const partnership = location.state as Partnership;
  const hasStatistic = partnership.status === PartnerShipStatus.ACCEPTED && partnerHabits && partnerRecords;
  const hasHabits = hasStatistic && partnerHabits.length > 0;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (partnership.status === PartnerShipStatus.ACCEPTED) {
      Promise.all([recordsForPartner(partnership.partner), habitsForPartner(partnership.partner)])
        .then(([records, habits]) => {
          setPartnerRecords(records);
          setPartnerHabits(habits);
        })
        .catch((e) => setError(translateError(e.message)))
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  async function end(partnership: Partnership) {
    await endPartnership(partnership);
    navigate(-1);
  }

  useLayout(
    () => ({ activeMenu: 'partners', title: partnership.partner.name, backButton: true, loading: isLoading }),
    [partnership, isLoading],
  );

  return (
    <div className="flex flex-col gap-5">
      {hasStatistic && (
        <>
          {hasHabits ? (
            <Statistic habits={partnerHabits} records={partnerRecords} />
          ) : (
            <span className="text-center text-base-content/70">
              {t('{{user}} does not yet track any habits.', { user: partnership.partner.name })}
            </span>
          )}
        </>
      )}

      {error && <Error msg={error} />}

      <button className="btn btn-error btn-lg" onClick={() => end(partnership)}>
        {t('End Partnership')}
      </button>
    </div>
  );
}
